import { apiClient } from './client';
import type {
  Job,
  JobFormData,
  JobFilters,
  JobsListResponse,
  PaginationConfig,
  DuplicateCheckResult,
  ScrapingQueueItem,
  QueueFilters,
  ScrapingQueueResponse,
  DuplicateGroup,
  DuplicateFilters,
  DuplicatesResponse,
  BulkUploadParseResults,
  JobStats,
  AssignedCustomer,
} from '@repo/types';

/**
 * Jobs API Client
 * Handles all job-related API requests
 * Integrated with backend API - all responses follow: { data: T, success: boolean, message?: string }
 */
export class JobsApi {
  /**
   * Get list of jobs with filters and pagination
   */
  async getJobs(filters?: JobFilters, pagination?: Partial<PaginationConfig>): Promise<JobsListResponse> {
    const params = new URLSearchParams();

    if (filters) {
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.scoutId) params.append('scoutId', filters.scoutId);
      if (filters.category) params.append('category', filters.category);
      if (filters.location) params.append('location', filters.location);
      if (filters.industry) filters.industry.forEach((i) => params.append('industry', i));
      if (filters.jobTitle) filters.jobTitle.forEach((t) => params.append('jobTitle', t));
      if (filters.jobType) params.append('jobType', filters.jobType);
      if (filters.workMode) params.append('workMode', filters.workMode);
      if (filters.source) params.append('source', filters.source);
      if (filters.search) params.append('search', filters.search);
      if (filters.dateRange) {
        params.append('dateFrom', filters.dateRange.from.toISOString());
        params.append('dateTo', filters.dateRange.to.toISOString());
      }
    }

    if (pagination) {
      if (pagination.page) params.append('page', pagination.page.toString());
      if (pagination.limit) params.append('limit', pagination.limit.toString());
      if (pagination.sortBy) params.append('sortBy', pagination.sortBy);
      if (pagination.sortOrder) params.append('sortOrder', pagination.sortOrder);
    }

    interface BackendJobResponse {
      postedByEmployee?: Job['postedBy'];
      postedBy?: Job['postedBy'];
      salaryMin?: string | number;
      salaryMax?: string | number;
      createdAt: string;
      updatedAt: string;
      expiryDate?: string | null;
      expiredAt?: string | null;
      postedDate?: string | null;
    }

    type BackendJob = Omit<Job, 'postedBy' | 'createdAt' | 'updatedAt' | 'expiryDate' | 'expiredAt' | 'postedDate' | 'salaryMin' | 'salaryMax'> & BackendJobResponse;

    // Backend returns: { data: { data: JobWithEmployee[], pagination: {...} }, success: boolean }
    // apiClient.get unwraps axios response, so response is: { data: { data: JobWithEmployee[], pagination: {...} }, success: boolean }
    const response = await apiClient.get<{
      data: BackendJob[];
      pagination: PaginationConfig;
    }>(`/jobs?${params.toString()}`);

    // Map backend response structure to frontend expected structure
    // Backend returns 'postedByEmployee', but frontend expects 'postedBy'
    const rawJobs = response.data.data || [];
    const jobs: Job[] = rawJobs.map((job: BackendJob) => ({
      ...job,
      postedBy: job.postedByEmployee || job.postedBy!,
      // Convert string numbers to numbers for salary fields
      salaryMin: job.salaryMin ? (typeof job.salaryMin === 'string' ? parseFloat(job.salaryMin) : job.salaryMin) : undefined,
      salaryMax: job.salaryMax ? (typeof job.salaryMax === 'string' ? parseFloat(job.salaryMax) : job.salaryMax) : undefined,
      // Convert string dates to Date objects
      createdAt: new Date(job.createdAt),
      updatedAt: new Date(job.updatedAt),
      expiryDate: job.expiryDate ? new Date(job.expiryDate) : undefined,
      expiredAt: job.expiredAt ? new Date(job.expiredAt) : undefined,
      postedDate: job.postedDate ? new Date(job.postedDate) : undefined,
    }));

    return {
      jobs,
      pagination: response.data.pagination || { page: 1, limit: 20, total: 0 },
    };
  }

  /**
   * Get single job by ID
   */
  async getJob(id: string): Promise<Job> {
    const response = await apiClient.get<Job>(`/jobs/${id}`);
    // API client returns ApiResponse<T> where T is Job, so response.data is the Job object
    return response.data;
  }

  /**
  * Create new job
  */
  async createJob(data: JobFormData): Promise<{ job: Job; duplicateWarning?: DuplicateCheckResult }> {
    // Transform benefits: if it's a string, convert to object; if empty, set to null
    const transformedData = {
      ...data,
      benefits: typeof data.benefits === 'string' 
        ? (data.benefits.trim() ? { description: data.benefits.trim() } : null)
        : data.benefits || null,
    };

    const response = await apiClient.post<{ job: Job; duplicateWarning?: DuplicateCheckResult }>(
      '/jobs',
      transformedData
    );
    // API client already unwraps response.data, so response is { data: { job, duplicateWarning }, success, message }
    return response.data;
  }

  /**
   * Update existing job
   */
  async updateJob(id: string, data: Partial<JobFormData>): Promise<Job> {
    // Transform benefits: if it's a string, convert to object; if empty, set to null
    const transformedData = {
      ...data,
      benefits: data.benefits !== undefined
        ? (typeof data.benefits === 'string' 
            ? (data.benefits.trim() ? { description: data.benefits.trim() } : null)
            : data.benefits || null)
        : undefined,
    };

    const response = await apiClient.patch<{ data: Job; success: boolean }>(`/jobs/${id}`, transformedData);
    return response.data.data;
  }

  /**
   * Delete job
   */
  async deleteJob(id: string): Promise<void> {
    await apiClient.delete<{ data: { id: string }; success: boolean }>(`/jobs/${id}`);
  }

  /**
   * Bulk update job status
   */
  async bulkUpdateStatus(jobIds: string[], status: 'active' | 'draft' | 'expired'): Promise<void> {
    await apiClient.patch('/jobs/bulk/status', { jobIds, status });
  }

  /**
   * Export jobs to CSV
   */
  async exportJobs(filters?: JobFilters): Promise<Blob> {
    const params = new URLSearchParams();

    if (filters) {
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.scoutId) params.append('scoutId', filters.scoutId);
      if (filters.category) params.append('category', filters.category);
      if (filters.location) params.append('location', filters.location);
      if (filters.industry) filters.industry.forEach((i) => params.append('industry', i));
      if (filters.jobType) params.append('jobType', filters.jobType);
      if (filters.workMode) params.append('workMode', filters.workMode);
      if (filters.source) params.append('source', filters.source);
      if (filters.search) params.append('search', filters.search);
      if (filters.dateRange) {
        params.append('dateFrom', filters.dateRange.from.toISOString());
        params.append('dateTo', filters.dateRange.to.toISOString());
      }
    }

    const axiosInstance = apiClient.getAxiosInstance();
    const response = await axiosInstance.get(`/jobs/export?${params.toString()}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * Check for duplicate jobs
   */
  async checkDuplicates(data: Partial<JobFormData>): Promise<DuplicateCheckResult> {
    const response = await apiClient.post<{ data: DuplicateCheckResult; success: boolean }>(
      '/jobs/duplicates/check',
      data
    );
    return response.data.data;
  }

  /**
   * Get autocomplete suggestions for skills
   */
  async getSkills(query: string): Promise<string[]> {
    const response = await apiClient.get<{ data: { skills: string[] }; success: boolean }>(
      `/jobs/skills?search=${query}`
    );
    return response.data.data.skills;
  }

  /**
   * Get list of categories
   */
  async getCategories(): Promise<string[]> {
    const response = await apiClient.get<{ data: { categories: string[] }; success: boolean }>('/jobs/categories');
    return response.data.data.categories;
  }

  /**
   * Get autocomplete suggestions for locations
   */
  async getLocations(query: string): Promise<string[]> {
    const response = await apiClient.get<{ data: { locations: string[] }; success: boolean }>(
      `/jobs/locations?search=${query}`
    );
    return response.data.data.locations;
  }

  /**
   * Get list of industries
   */
  async getIndustries(): Promise<string[]> {
    const response = await apiClient.get<{ data: { industries: string[] }; success: boolean }>('/jobs/industries');
    return response.data.data.industries;
  }

  // ==================== Job Statistics ====================

  /**
   * Get job statistics
   */
  async getJobStats(): Promise<JobStats> {
    // /jobs/stats is single-wrapped by sendSuccess ({ success, data: stats }),
    // so apiClient.get (which unwraps the axios layer) leaves the stats at
    // `.data` — NOT `.data.data` like the double-wrapped list endpoints.
    const response = await apiClient.get<JobStats>('/jobs/stats');
    return response.data;
  }

  // ==================== Scraping Queue ====================

  /**
   * Queue a job for URL scraping
   */
  async scrapeJob(
    url: string,
    formInputData?: Partial<JobFormData>,
    priority?: number
  ): Promise<ScrapingQueueItem> {
    const response = await apiClient.post<ScrapingQueueItem>('/jobs/scrape', {
      sourceUrl: url,
      formInputData,
      priority: priority || 5,
    });
    return response.data;
  }

  /**
   * Get scraping queue items with filters
   */
  async getScrapingQueue(
    filters?: QueueFilters,
    pagination?: Partial<PaginationConfig>
  ): Promise<ScrapingQueueResponse> {
    const params = new URLSearchParams();

    if (filters) {
      if (filters.status) filters.status.forEach((s) => params.append('status[]', s));
      if (filters.outcome) filters.outcome.forEach((o) => params.append('outcome[]', o));
      if (filters.employeeId) params.append('employeeId', filters.employeeId);
      if (filters.priority) params.append('priority', filters.priority.toString());
      if (filters.dateRange) {
        params.append('dateFrom', filters.dateRange.from.toISOString());
        params.append('dateTo', filters.dateRange.to.toISOString());
      }
    }

    if (pagination) {
      if (pagination.page) params.append('page', pagination.page.toString());
      if (pagination.limit) params.append('limit', pagination.limit.toString());
    }

    const response = await apiClient.get<ScrapingQueueResponse>(`/jobs/scraping-queue?${params.toString()}`);
    return response.data;
  }

  /**
   * Get single scraping queue item
   */
  async getScrapingQueueItem(id: string): Promise<ScrapingQueueItem> {
    const response = await apiClient.get<ScrapingQueueItem>(`/jobs/scraping-queue/${id}`);
    return response.data;
  }

  /**
   * Retry failed scraping job
   */
  async retryScrapingJob(id: string): Promise<ScrapingQueueItem> {
    const response = await apiClient.post<ScrapingQueueItem>(`/jobs/scraping-queue/${id}/retry`);
    return response.data;
  }

  /**
   * Cancel pending scraping job
   */
  async cancelScrapingJob(id: string): Promise<void> {
    await apiClient.delete(`/jobs/scraping-queue/${id}/cancel`);
  }

  /**
   * Delete scraping queue item
   */
  async deleteQueueItem(id: string): Promise<void> {
    await apiClient.delete(`/jobs/scraping-queue/${id}`);
  }

  /**
   * Update scraping queue item priority
   */
  async updateQueuePriority(id: string, priority: number): Promise<ScrapingQueueItem> {
    const response = await apiClient.patch<ScrapingQueueItem>(`/jobs/scraping-queue/${id}/priority`, { priority });
    return response.data;
  }

  /**
   * Bulk retry all failed scraping jobs
   */
  async bulkRetryFailed(): Promise<void> {
    await apiClient.post('/jobs/scraping-queue/bulk-retry');
  }

  /**
   * Bulk cancel all pending scraping jobs
   */
  async bulkCancelPending(): Promise<void> {
    await apiClient.post('/jobs/scraping-queue/bulk-cancel');
  }

  // ==================== Duplicates ====================

  /**
   * Get duplicate job groups
   */
  async getDuplicates(
    filters?: DuplicateFilters,
    pagination?: Partial<PaginationConfig>
  ): Promise<DuplicatesResponse> {
    const params = new URLSearchParams();

    if (filters) {
      if (filters.minSimilarity) params.append('minSimilarity', filters.minSimilarity.toString());
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.dateRange) {
        params.append('dateFrom', filters.dateRange.from.toISOString());
        params.append('dateTo', filters.dateRange.to.toISOString());
      }
    }

    if (pagination) {
      if (pagination.page) params.append('page', pagination.page.toString());
      if (pagination.limit) params.append('limit', pagination.limit.toString());
    }

    const response = await apiClient.get<DuplicatesResponse>(
      `/jobs/duplicates?${params.toString()}`
    );

    return response.data;
  }

  /**
   * Get duplicate group comparison details
   */
  async getDuplicateComparison(groupId: string): Promise<DuplicateGroup> {
    const response = await apiClient.get<{ data: DuplicateGroup; success: boolean }>(`/jobs/duplicates/${groupId}`);
    return response.data.data;
  }

  /**
   * Resolve duplicate group (keep one, delete others)
   */
  async resolveDuplicates(groupId: string, action: string, keptJobId?: string, comments?: string): Promise<void> {
    await apiClient.post(`/jobs/duplicates/${groupId}/resolve`, { action, keptJobId, comments });
  }

  /**
   * Merge duplicate jobs
   */
  async mergeDuplicates(jobIds: string[], mergedData: JobFormData): Promise<Job> {
    const response = await apiClient.post<{ data: { jobId: string }; success: boolean }>('/jobs/duplicates/merge', {
      jobIds,
      mergedData,
    });
    // Return merged job by fetching it
    return this.getJob(response.data.data.jobId);
  }

  /**
   * Mark duplicate group as not duplicate
   */
  async markNotDuplicate(groupId: string, comments?: string): Promise<void> {
    await apiClient.patch(`/jobs/duplicates/${groupId}/not-duplicate`, { comments });
  }

  /**
   * Get all duplicate groups for a specific job
   */
  async getJobDuplicates(jobId: string): Promise<DuplicatesResponse> {
    type BackendJobInGroup = {
      postedByEmployee?: { id: string; name: string; role: string };
      postedBy?: string | { id: string; name: string; role: string };
      [key: string]: unknown;
    };

    const response = await apiClient.get<DuplicateGroup[]>(
      `/jobs/${jobId}/duplicates`
    );

    const groups = (response.data || []).map((group) => ({
      ...group,
      jobs: group.jobs.map((job) => {
        const raw = job as unknown as BackendJobInGroup;
        return {
          ...job,
          postedBy: raw.postedByEmployee ?? (raw.postedBy as { id: string; name: string; role: string }),
        };
      }),
    }));

    return {
      duplicateGroups: groups,
      pagination: { page: 1, limit: 50, total: groups.length },
    };
  }

  // ==================== Bulk Upload ====================

  /**
   * Parse and validate CSV file
   */
  async parseBulkUpload(file: File): Promise<BulkUploadParseResults> {
    // Read file content as text
    const csvContent = await file.text();

    const response = await apiClient.post<{ data: BulkUploadParseResults; success: boolean }>(
      '/jobs/bulk-upload/parse',
      { csvContent }
    );
    return response.data.data;
  }

  /**
   * Import validated jobs from CSV
   */
  async importBulkUpload(rows: JobFormData[]): Promise<{ imported: number; failed: number; jobIds: string[] }> {
    const response = await apiClient.post<{
      data: { imported: number; failed: number; jobIds: string[] };
      success: boolean;
    }>('/jobs/bulk-upload/import', { rows });
    return response.data.data;
  }

  /**
   * Download CSV template
   */
  async downloadTemplate(): Promise<Blob> {
    const axiosInstance = apiClient.getAxiosInstance();
    const response = await axiosInstance.get('/jobs/bulk-upload/template', {
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * Get assigned customers for a job
   */
  async getAssignedCustomers(jobId: string): Promise<AssignedCustomer[]> {
    const response = await apiClient.get<AssignedCustomer[]>(`/jobs/${jobId}/assigned-customers`);
    return response.data;
  }
}

// Export singleton instance
export const jobsApi = new JobsApi();